import { useMemo } from 'react';
import { Sparkles, ExternalLink, CheckCircle2, Circle, Star } from 'lucide-react';

export default function DashboardHabitsColumn({
  habits,
  isChecked,
  toggleCheck,
  getCheckoffFrequency
}) {
  // Sort habits: pinned first, then unchecked, then checked
  const sortedHabits = useMemo(() => {
    return [...habits].sort((a, b) => {
      // Pinned first
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      // Then unchecked before checked
      const aChecked = isChecked(a.id, getCheckoffFrequency(a));
      const bChecked = isChecked(b.id, getCheckoffFrequency(b));
      if (!aChecked && bChecked) return -1;
      if (aChecked && !bChecked) return 1;

      return 0;
    });
  }, [habits, isChecked, getCheckoffFrequency]);

  // Calculate progress
  const progress = useMemo(() => {
    const total = habits.length;
    const checked = habits.filter(h => isChecked(h.id, getCheckoffFrequency(h))).length;
    return { checked, total };
  }, [habits, isChecked, getCheckoffFrequency]);

  const handleToggle = (habit, e) => {
    e.stopPropagation();
    toggleCheck(habit.id, getCheckoffFrequency(habit));
  };

  const handleOpenLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="dashboard-column">
      {/* Header */}
      <div className="dashboard-column-header">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-accent" />
          <h2 className="text-sm font-semibold text-text-primary">Today's Habits</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-tertiary">
            {progress.checked}/{progress.total}
          </span>
          {progress.total > 0 && (
            <div className="w-16 h-1.5 bg-surface-hover rounded-full overflow-hidden">
              <div
                className="h-full bg-accent rounded-full transition-all duration-300"
                style={{ width: `${(progress.checked / progress.total) * 100}%` }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="dashboard-column-content">
        {sortedHabits.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Sparkles size={24} className="text-text-muted mb-2" />
            <p className="text-sm text-text-secondary">No habits for today</p>
            <p className="text-[10px] text-text-tertiary mt-1">
              Daily and scheduled weekly habits appear here
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {sortedHabits.map((habit) => {
              const checked = isChecked(habit.id, getCheckoffFrequency(habit));
              return (
                <div
                  key={habit.id}
                  className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer transition-all
                    ${checked ? 'bg-surface-hover/50 opacity-60' : 'bg-surface-card hover:bg-surface-hover'}
                    border border-border/50`}
                  onClick={() => handleOpenLink(habit.url)}
                >
                  {/* Checkbox */}
                  <button
                    onClick={(e) => handleToggle(habit, e)}
                    className="flex-shrink-0 bg-transparent border-none cursor-pointer p-0"
                  >
                    {checked ? (
                      <CheckCircle2 size={18} className="text-green-500" />
                    ) : (
                      <Circle size={18} className="text-text-muted hover:text-accent transition-colors" />
                    )}
                  </button>

                  {/* Name */}
                  <span className={`flex-1 text-sm truncate ${checked ? 'line-through text-text-muted' : 'text-text-primary'}`}>
                    {habit.name}
                  </span>

                  {/* Indicators */}
                  <div className="flex items-center gap-1.5">
                    {habit.pinned && (
                      <Star size={12} className="text-accent fill-accent" />
                    )}
                    <ExternalLink size={12} className="text-text-tertiary" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
